// The Home chat's composer controls (decision D36 section B): project (or a new one), agent, integrations as context
// hints, and the working mode. They travel in the turn context (mode, agent_profile_id, integration_ids); the project
// picks the conversation's scope. The mode is enforced server-side; here it is only chosen and remembered per browser.
import { useEffect, useId, useState, type Ref } from "react";
import { ASSISTANT_MODES, type AgentRegistryEntry, type AssistantMode, type ProjectSummary } from "@maestros/contracts";
import { listAgents } from "../../api/endpoints-agents.ts";
import { useI18n } from "../../i18n/index.tsx";
import { CpuIcon, FolderIcon, GaugeIcon, PlugIcon } from "../../components/icons.tsx";
import { loadIntegrationHints, type IntegrationHints } from "./ComposerPanels.tsx";

export interface HomeChatOptions {
  projectId: string | null;
  agentId: string | null;
  integrationIds: string[];
  mode: AssistantMode;
}

export const NEW_PROJECT = "__new__";

export function HomeControls({
  value,
  onChange,
  projects,
  onNewProject,
  agentRef,
  modeRef,
  loadAgents = (signal) => listAgents(signal).then((r) => r.items),
  loadHints = loadIntegrationHints,
}: {
  value: HomeChatOptions;
  onChange: (next: HomeChatOptions) => void;
  projects: readonly Pick<ProjectSummary, "id" | "name">[];
  onNewProject: () => void;
  agentRef?: Ref<HTMLSelectElement>;
  modeRef?: Ref<HTMLSelectElement>;
  loadAgents?: (signal?: AbortSignal) => Promise<AgentRegistryEntry[]>;
  loadHints?: (signal?: AbortSignal) => Promise<IntegrationHints>;
}) {
  const { t } = useI18n();
  const uid = useId();
  const [agents, setAgents] = useState<AgentRegistryEntry[]>([]);
  const [hintsOpen, setHintsOpen] = useState(false);
  const [hints, setHints] = useState<IntegrationHints>({ state: "loading" });

  useEffect(() => {
    const controller = new AbortController();
    loadAgents(controller.signal).then((items) => setAgents(items.filter((a) => a.state === "active")), () => setAgents([]));
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hintsOpen || hints.state !== "loading") return;
    const controller = new AbortController();
    loadHints(controller.signal).then(setHints, () => undefined);
    return () => controller.abort();
  }, [hintsOpen, hints.state, loadHints]);

  const set = (patch: Partial<HomeChatOptions>) => onChange({ ...value, ...patch });
  const toggleIntegration = (id: string, on: boolean) => set({ integrationIds: on ? [...value.integrationIds, id].slice(0, 10) : value.integrationIds.filter((x) => x !== id) });

  return (
    <div className="home-controls" role="group" aria-label={t("composer.home.controls")} data-testid="home-controls">
      <label className="home-control">
        <FolderIcon size={16} />
        <span className="sr-only">{t("composer.home.project")}</span>
        <select
          value={value.projectId ?? ""}
          onChange={(e) => {
            if (e.target.value === NEW_PROJECT) onNewProject();
            else set({ projectId: e.target.value || null });
          }}
        >
          <option value="">{t("composer.home.projectAll")}</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
          <option value={NEW_PROJECT}>{t("composer.home.projectNew")}</option>
        </select>
      </label>
      <label className="home-control">
        <CpuIcon size={16} />
        <span className="sr-only">{t("composer.home.agent")}</span>
        <select ref={agentRef} value={value.agentId ?? ""} onChange={(e) => set({ agentId: e.target.value || null })}>
          <option value="">{t("composer.home.agentDefault")}</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </label>
      <details className="home-control home-integrations" open={hintsOpen} onToggle={(e) => setHintsOpen((e.target as HTMLDetailsElement).open)}>
        <summary>
          <PlugIcon size={16} />
          <span>{value.integrationIds.length > 0 ? t("composer.home.integrationsCount", { n: value.integrationIds.length }) : t("composer.home.integrations")}</span>
        </summary>
        <div className="home-integrations-menu" data-testid="home-integrations">
          <p className="subtle">{t("composer.home.integrationsHint")}</p>
          {hints.state === "loading" ? <p className="subtle">{t("composer.mcp.loading")}</p> : null}
          {hints.state === "forbidden" ? <p className="subtle">{t("composer.mcp.forbidden")}</p> : null}
          {hints.state === "failed" ? <p className="subtle">{t("composer.mcp.failed")}</p> : null}
          {hints.state === "ready" && hints.items.length === 0 ? <p className="subtle">{t("composer.mcp.empty")}</p> : null}
          {hints.state === "ready"
            ? hints.items.map((i) => (
                <label key={i.id} className="checkbox-row">
                  <input type="checkbox" checked={value.integrationIds.includes(i.id)} onChange={(e) => toggleIntegration(i.id, e.target.checked)} />
                  <span>
                    {i.name} <span className="subtle">({i.detail})</span>
                  </span>
                </label>
              ))
            : null}
        </div>
      </details>
      <label className="home-control">
        <GaugeIcon size={16} />
        <span className="sr-only">{t("composer.home.mode")}</span>
        <select ref={modeRef} value={value.mode} onChange={(e) => set({ mode: e.target.value as AssistantMode })} aria-describedby={`${uid}-mode`} title={t(`composer.modeHint.${value.mode}`)}>
          {ASSISTANT_MODES.map((m) => (
            <option key={m} value={m}>
              {t(`composer.mode.${m}`)}
            </option>
          ))}
        </select>
      </label>
      <p id={`${uid}-mode`} className="sr-only">
        {t(`composer.modeHint.${value.mode}`)}
      </p>
    </div>
  );
}
